/**
 * Audio Queue Service
 *
 * Queues audio blob URLs and plays them sequentially.
 * Revokes URLs after playback to free memory.
 * Notifies listeners when playing state changes.
 */

let queue: string[] = []
let playing = false
let currentAudio: HTMLAudioElement | null = null
let stopped = false
let onStateChange: ((isPlaying: boolean) => void) | null = null

export function setOnStateChange(cb: ((isPlaying: boolean) => void) | null): void {
  onStateChange = cb
}

function updateState(newPlaying: boolean): void {
  if (playing !== newPlaying) {
    playing = newPlaying
    onStateChange?.(playing)
  }
}

export function enqueueAudio(blobUrl: string): void {
  queue.push(blobUrl)
  if (!playing && !stopped) {
    playNext()
  }
}

function playNext(): void {
  if (queue.length === 0 || stopped) {
    updateState(false)
    return
  }

  updateState(true)
  const url = queue.shift()!

  currentAudio = new Audio(url)

  const cleanup = () => {
    URL.revokeObjectURL(url)
    currentAudio = null
    playNext()
  }

  currentAudio.onended = cleanup
  currentAudio.onerror = (e) => {
    console.warn('Audio playback error, skipping segment:', e)
    cleanup()
  }
  currentAudio.play().catch((e) => {
    console.warn('Audio play() failed:', e)
    cleanup()
  })
}

export function stopAudioQueue(): void {
  stopped = true
  // Revoke all pending URLs
  for (const url of queue) {
    URL.revokeObjectURL(url)
  }
  queue = []
  if (currentAudio) {
    const src = currentAudio.src
    currentAudio.pause()
    currentAudio.src = ''
    currentAudio = null
    if (src.startsWith('blob:')) URL.revokeObjectURL(src)
  }
  updateState(false)
}

export function resetAudioQueue(): void {
  stopAudioQueue()
  stopped = false
}

export function isAudioPlaying(): boolean {
  return playing
}
